import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Dialog, Snackbar } from '@core/material';
import { translateCommonErrorStatus, StatusMessage } from '@core/data';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Organization, StorageRegime } from '@core/data';
import { AuthState } from '@app/auth';
import { LIBRARY } from '@app/evaluation/library';
import { Crop, Library } from '@library';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import {
    CreateCustomRegime,
    DeleteCustomRegime,
    InitStorageRegimeState,
    StorageRegimeState,
    UpdateCustomRegime,
} from './storage-regime.state';
import {
    InitStorageRegimeIndex,
    LoadStorageRegimeIndex,
    PageStorageRegimeIndex,
    SearchStorageRegimeIndex,
    SortStorageRegimeIndex,
    StorageRegimeIndexState,
    StorageRegimeIndexStateModel,
} from './storage-regime-index.state';
import { StorageRegimeFormDialog, StorageRegimeFormDialogData } from './storage-regime-form.dialog';

@Component({
    selector: 'pv-storage-regime-index-view',
    templateUrl: './storage-regime-index.view.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-storage-regime-index-view',
    },
})
export class StorageRegimeIndexView implements OnInit, OnDestroy {

    // Full, unpaginated Custom+Legacy list — still needed by the regime-builder dialog's
    // "start from a Legacy Regime" selector. Not what the table itself renders.
    @Select(StorageRegimeState.regimes)
    regimes$: Observable<StorageRegime[]>;

    @Select(StorageRegimeIndexState.state)
    indexState$: Observable<StorageRegimeIndexStateModel>;

    @Select(AuthState.selectedOrg)
    selectedOrg$: Observable<Organization>;

    searchControl = new FormControl(null);

    // Footer sort dropdown + direction toggle, matching the Report/Protocol index pattern.
    columnControl = new FormControl('label');
    orderControl = new FormControl('asc');
    sortFormGroup = new FormGroup({
        column: this.columnControl,
        order: this.orderControl,
    });

    readonly customColumns = ['label', 'actions', 'crop', 'stages', 'timestamps'];

    isReadonly = false;
    orgKey: string;

    private _destroy$ = new Subject();

    constructor(
        private _route: ActivatedRoute,
        private _store: Store,
        private _dialogs: Dialog,
        private _snackbar: Snackbar,
        @Inject(LIBRARY) private _library: Library,
    ) {
    }

    ngOnInit() {
        this._route.paramMap
            .pipe(takeUntil(this._destroy$))
            .subscribe(params => {
                this.orgKey = params.get('orgKey');
                this._store.dispatch(new InitStorageRegimeState(this.orgKey));
                this._store.dispatch(new InitStorageRegimeIndex(this.orgKey));
            });

        // Reflects state-driven sort changes (e.g. a column-header click) back into the footer
        // dropdown without re-triggering its own valueChanges handler below.
        this.indexState$
            .pipe(takeUntil(this._destroy$))
            .subscribe(state => this.sortFormGroup.patchValue(state.sort, { emitEvent: false }));

        this.sortFormGroup.valueChanges
            .pipe(debounceTime(200), takeUntil(this._destroy$))
            .subscribe(value => this._store.dispatch(new SortStorageRegimeIndex(value.column, value.order)));

        this.searchControl.valueChanges
            .pipe(debounceTime(300), takeUntil(this._destroy$))
            .subscribe(search => this._store.dispatch(new SearchStorageRegimeIndex(search)));

        this.selectedOrg$
            .pipe(takeUntil(this._destroy$))
            .subscribe(org => {
                this.isReadonly = org?.access.role === 'r' || org?.access.role === 'guest';
            });
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // Table column header click — mirrors the footer dropdown onto the same action.
    tableSortChange(sort: Sort) {
        if (sort.direction) {
            this._store.dispatch(new SortStorageRegimeIndex(sort.active, sort.direction));
        }
    }

    pageChange(event: PageEvent) {
        this._store.dispatch(new PageStorageRegimeIndex(event.pageIndex, event.pageSize));
    }

    toggleSortOrder(event: MouseEvent) {
        event.stopPropagation();
        this.orderControl.setValue(this.orderControl.value === 'asc' ? 'desc' : 'asc');
    }

    // A crop the library no longer offers still needs to resolve to something rather than throwing.
    regimeCrops(regime: StorageRegime): Crop[] {
        return (regime.cropIds || [])
            .map(cropId => this._library.crops.get(cropId))
            .filter(crop => !!crop);
    }

    clearFilters() {
        this.searchControl.reset();
    }

    reload() {
        this._store.dispatch(new InitStorageRegimeState(this.orgKey));
        this._store.dispatch(new LoadStorageRegimeIndex());
    }

    addRegime(regimes: StorageRegime[]) {
        const data: StorageRegimeFormDialogData = {
            ownerOrgKey: this.orgKey,
            availableRegimes: regimes || [],
        };

        this._dialogs.openFullscreen(StorageRegimeFormDialog, { data })
            .afterClosed()
            .subscribe(result => {
                if (!result) return;

                this._store.dispatch(new CreateCustomRegime(this.orgKey, result)).subscribe({
                    next: () => {
                        this._snackbar.info('Custom Regime created');
                        this._store.dispatch(new LoadStorageRegimeIndex());
                    },
                    error: error => this._snackbar.error(StatusMessage[translateCommonErrorStatus(error)]),
                });
            });
    }

    editRegime(regime: StorageRegime, regimes: StorageRegime[]) {
        const data: StorageRegimeFormDialogData = {
            ownerOrgKey: this.orgKey,
            regime,
            availableRegimes: regimes || [],
        };

        this._dialogs.openFullscreen(StorageRegimeFormDialog, { data })
            .afterClosed()
            .subscribe(result => {
                if (!result) return;

                this._store.dispatch(new UpdateCustomRegime(regime.key, result)).subscribe({
                    next: () => {
                        this._snackbar.info('Custom Regime updated');
                        this._store.dispatch(new LoadStorageRegimeIndex());
                    },
                    error: error => this._snackbar.error(StatusMessage[translateCommonErrorStatus(error)]),
                });
            });
    }

    removeRegime(regime: StorageRegime) {
        this._dialogs.confirm(
            `Delete ${regime.config?.label}`,
            'Are you sure you want to delete this Custom Regime? Samples already using it keep their already-generated schedule.',
            'Delete',
            'Cancel',
        )
            .afterClosed()
            .subscribe(result => {
                if (!result) return;

                this._store.dispatch(new DeleteCustomRegime(regime)).subscribe({
                    next: () => {
                        this._snackbar.info('Custom Regime deleted');
                        this._store.dispatch(new LoadStorageRegimeIndex());
                    },
                    error: error => this._snackbar.error(StatusMessage[translateCommonErrorStatus(error)]),
                });
            });
    }
}
